import { FormEvent, ReactNode, useEffect, useMemo, useState } from "react";
import {
  browserLocalPersistence,
  onAuthStateChanged,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signOut,
  User,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import {
  ArrowUpRight,
  Eye,
  EyeOff,
  FolderKanban,
  Loader2 as LoaderCircle,
  LockKeyhole,
  LogOut,
  Plus,
  Save,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";
import { Project } from "../../data/projects";
import { firebaseAuth, firestoreDb, isFirebaseConfigured } from "../../lib/firebase";
import {
  deleteProject,
  saveProject,
  subscribeToAllProjects,
} from "../../services/adminProjects";
import { uploadImageToR2 } from "../../services/r2Upload";
import "../../stylesheets/admin.css";

type AccessState = "checking" | "signed-out" | "authorized" | "denied" | "error";

type ProjectFormState = {
  category: string;
  description: string;
  features: string;
  id: string;
  images: string;
  name: string;
  order: string;
  published: boolean;
  result: string;
  stack: string;
  url: string;
};

const EMPTY_FORM: ProjectFormState = {
  category: "",
  description: "",
  features: "",
  id: "",
  images: "",
  name: "",
  order: "1",
  published: false,
  result: "",
  stack: "",
  url: "",
};

function projectToForm(project: Project): ProjectFormState {
  return {
    ...project,
    features: project.features.join("\n"),
    images: project.images.join("\n"),
    order: String(project.order),
    stack: project.stack.join(", "),
  };
}

function splitLines(value: string) {
  return value
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);
}

function formToProject(form: ProjectFormState): Project {
  return {
    category: form.category.trim(),
    description: form.description.trim(),
    features: splitLines(form.features),
    id: form.id.trim(),
    images: splitLines(form.images),
    name: form.name.trim(),
    order: Number(form.order),
    published: form.published,
    result: form.result.trim(),
    stack: form.stack
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean),
    url: form.url.trim(),
  };
}

function validateProject(project: Project) {
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(project.id)) {
    return "The project ID must use lowercase letters, numbers, and hyphens.";
  }

  if (!project.name || !project.category || !project.description || !project.url) {
    return "Name, category, description, and live URL are required.";
  }

  if (!Number.isInteger(project.order) || project.order < 0) {
    return "Order must be a whole number of zero or greater.";
  }

  if (project.images.length === 0) {
    return "Add at least one image URL.";
  }

  if (project.images.length > 8) {
    return "A project can contain at most eight images.";
  }

  return null;
}

function Field({
  children,
  htmlFor,
  label,
  wide = false,
}: {
  children: ReactNode;
  htmlFor: string;
  label: string;
  wide?: boolean;
}) {
  return (
    <div className={`admin-field${wide ? " admin-field--wide" : ""}`}>
      <label htmlFor={htmlFor}>{label}</label>
      {children}
    </div>
  );
}

function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const handleSignIn = async (event: FormEvent) => {
    event.preventDefault();

    if (!firebaseAuth) {
      setError("Firebase Authentication is not configured.");
      return;
    }

    setBusy(true);
    setError("");
    setMessage("");

    try {
      await setPersistence(firebaseAuth, browserLocalPersistence);
      await signInWithEmailAndPassword(firebaseAuth, email.trim(), password);
    } catch {
      setError("The email or password is incorrect.");
    } finally {
      setBusy(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!firebaseAuth || !email.trim()) {
      setError("Enter your admin email first.");
      return;
    }

    setBusy(true);
    setError("");
    setMessage("");

    try {
      await sendPasswordResetEmail(firebaseAuth, email.trim());
      setMessage("Password setup email sent.");
    } catch {
      setError("The password email could not be sent.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="admin-auth-page">
      <section className="admin-auth-brand" aria-label="Portfolio admin">
        <div className="admin-brand-mark">MKT</div>
        <div>
          <p>Private workspace</p>
          <h1>Portfolio Admin</h1>
        </div>
      </section>

      <section className="admin-auth-panel">
        <div className="admin-auth-form-wrap">
          <div className="admin-auth-icon" aria-hidden="true">
            <LockKeyhole size={22} />
          </div>
          <p className="admin-eyebrow">Authorized access</p>
          <h2>Sign in</h2>

          <form onSubmit={handleSignIn} className="admin-auth-form">
            <Field htmlFor="admin-email" label="Email">
              <input
                autoComplete="username"
                id="admin-email"
                onChange={(event) => setEmail(event.target.value)}
                required
                type="email"
                value={email}
              />
            </Field>

            <Field htmlFor="admin-password" label="Password">
              <div className="admin-password-field">
                <input
                  autoComplete="current-password"
                  id="admin-password"
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  type={showPassword ? "text" : "password"}
                  value={password}
                />
                <button
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((current) => !current)}
                  title={showPassword ? "Hide password" : "Show password"}
                  type="button"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </Field>

            {error && <p className="admin-form-message admin-form-message--error">{error}</p>}
            {message && <p className="admin-form-message admin-form-message--success">{message}</p>}

            <button className="admin-primary-button" disabled={busy} type="submit">
              {busy ? <LoaderCircle className="admin-spin" size={18} /> : <LockKeyhole size={18} />}
              Sign in
            </button>

            <button
              className="admin-text-button"
              disabled={busy}
              onClick={handlePasswordReset}
              type="button"
            >
              Set or reset password
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}

function AccessDenied({ user }: { user: User }) {
  return (
    <main className="admin-state-page">
      <LockKeyhole size={28} />
      <p className="admin-eyebrow">Access denied</p>
      <h1>This account is not an administrator.</h1>
      <p>{user.email}</p>
      <button
        className="admin-secondary-button"
        onClick={() => firebaseAuth && signOut(firebaseAuth)}
        type="button"
      >
        <LogOut size={17} />
        Sign out
      </button>
    </main>
  );
}

function AdminDashboard({ user }: { user: User }) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState<ProjectFormState>(EMPTY_FORM);
  const [isNew, setIsNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string>("");
  const [isDragging, setIsDragging] = useState(false);

  const handleFileUpload = async (files: FileList | File[]) => {
    if (!files || files.length === 0) return;

    const projectId = form.id.trim() || "general";
    setUploading(true);
    setUploadStatus(`Uploading ${files.length} file(s) to Cloudflare R2...`);
    setError("");

    const uploadedUrls: string[] = [];
    const errors: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        setUploadStatus(`Uploading (${i + 1}/${files.length}): ${file.name}...`);
        const result = await uploadImageToR2(file, projectId);
        uploadedUrls.push(result.url);
      } catch (err: any) {
        errors.push(`${file.name}: ${err?.message || "Upload failed"}`);
      }
    }

    if (uploadedUrls.length > 0) {
      const currentImages = splitLines(form.images);
      const newImages = [...currentImages, ...uploadedUrls];
      updateField("images", newImages.join("\n"));
      setMessage(`Successfully uploaded ${uploadedUrls.length} image(s) to Cloudflare R2.`);
    }

    if (errors.length > 0) {
      setError(`Some uploads failed:\n${errors.join("\n")}`);
    }

    setUploading(false);
    setUploadStatus("");
  };

  const removeImage = (indexToRemove: number) => {
    const currentImages = splitLines(form.images);
    const filtered = currentImages.filter((_, idx) => idx !== indexToRemove);
    updateField("images", filtered.join("\n"));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(Array.from(e.dataTransfer.files));
    }
  };

  useEffect(() => {
    return subscribeToAllProjects(
      (nextProjects) => {
        setProjects(nextProjects);
        setLoading(false);
      },
      () => {
        setError("Projects could not be loaded.");
        setLoading(false);
      },
    );
  }, []);

  useEffect(() => {
    if (isNew || selectedId || projects.length === 0) {
      return;
    }

    setSelectedId(projects[0].id);
    setForm(projectToForm(projects[0]));
  }, [isNew, projects, selectedId]);

  const previewImage = useMemo(() => splitLines(form.images)[0] || "", [form.images]);

  const updateField = <Key extends keyof ProjectFormState>(
    key: Key,
    value: ProjectFormState[Key],
  ) => {
    setForm((current) => ({ ...current, [key]: value }));
    setError("");
    setMessage("");
  };

  const handleSelect = (project: Project) => {
    setSelectedId(project.id);
    setForm(projectToForm(project));
    setIsNew(false);
    setError("");
    setMessage("");
  };

  const handleNew = () => {
    const nextOrder = projects.reduce((highest, project) => Math.max(highest, project.order), 0) + 1;
    setSelectedId(null);
    setForm({ ...EMPTY_FORM, order: String(nextOrder) });
    setIsNew(true);
    setError("");
    setMessage("");
  };

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    const project = formToProject(form);
    const validationError = validateProject(project);

    if (validationError) {
      setError(validationError);
      return;
    }

    setBusy(true);
    setError("");
    setMessage("");

    try {
      await saveProject(project);
      setSelectedId(project.id);
      setIsNew(false);
      setMessage("Project saved.");
    } catch {
      setError("The project could not be saved.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId || !window.confirm(`Delete “${form.name}”? This cannot be undone.`)) {
      return;
    }

    setBusy(true);
    setError("");

    try {
      await deleteProject(selectedId);
      const remaining = projects.filter((project) => project.id !== selectedId);
      const nextProject = remaining[0];
      setSelectedId(nextProject?.id || null);
      setForm(nextProject ? projectToForm(nextProject) : EMPTY_FORM);
      setIsNew(!nextProject);
      setMessage("Project deleted.");
    } catch {
      setError("The project could not be deleted.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="admin-app">
      <header className="admin-topbar">
        <div className="admin-topbar-brand">
          <span className="admin-brand-mark admin-brand-mark--small">MKT</span>
          <div>
            <strong>Portfolio Admin</strong>
            <span>Projects</span>
          </div>
        </div>

        <div className="admin-account">
          <a href="/" target="_blank" rel="noreferrer">
            View site
            <ArrowUpRight size={15} />
          </a>
          <span className="admin-account-email">{user.email}</span>
          <button
            aria-label="Sign out"
            onClick={() => firebaseAuth && signOut(firebaseAuth)}
            title="Sign out"
            type="button"
          >
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <div className="admin-workspace">
        <aside className="admin-project-nav">
          <div className="admin-project-nav-heading">
            <div>
              <p className="admin-eyebrow">Content</p>
              <h1>Projects</h1>
            </div>
            <span>{projects.length}</span>
          </div>

          <button className="admin-add-button" onClick={handleNew} type="button">
            <Plus size={17} />
            New project
          </button>

          <div className="admin-project-list" aria-label="Projects">
            {loading && (
              <div className="admin-list-loading">
                <LoaderCircle className="admin-spin" size={18} />
                Loading
              </div>
            )}

            {!loading && projects.length === 0 && (
              <p className="admin-empty-list">No projects yet.</p>
            )}

            {projects.map((project) => (
              <button
                className={selectedId === project.id && !isNew ? "is-active" : ""}
                key={project.id}
                onClick={() => handleSelect(project)}
                type="button"
              >
                <span className="admin-project-order">{String(project.order).padStart(2, "0")}</span>
                <span className="admin-project-list-copy">
                  <strong>{project.name}</strong>
                  <small>{project.category}</small>
                </span>
                <span
                  aria-label={project.published ? "Published" : "Draft"}
                  className={`admin-status-dot${project.published ? " is-published" : ""}`}
                  title={project.published ? "Published" : "Draft"}
                />
              </button>
            ))}
          </div>
        </aside>

        <section className="admin-editor">
          <form onSubmit={handleSave}>
            <div className="admin-editor-heading">
              <div>
                <p className="admin-eyebrow">{isNew ? "New entry" : "Edit entry"}</p>
                <h2>{form.name || "Untitled project"}</h2>
              </div>

              <div className="admin-editor-actions">
                {!isNew && (
                  <button
                    aria-label="Delete project"
                    className="admin-icon-button admin-icon-button--danger"
                    disabled={busy}
                    onClick={handleDelete}
                    title="Delete project"
                    type="button"
                  >
                    <Trash2 size={18} />
                  </button>
                )}
                <button className="admin-primary-button admin-save-button" disabled={busy} type="submit">
                  {busy ? <LoaderCircle className="admin-spin" size={18} /> : <Save size={18} />}
                  Save
                </button>
              </div>
            </div>

            {error && <p className="admin-form-message admin-form-message--error">{error}</p>}
            {message && <p className="admin-form-message admin-form-message--success">{message}</p>}

            <div className="admin-form-grid">
              <Field htmlFor="project-name" label="Project name">
                <input
                  id="project-name"
                  onChange={(event) => updateField("name", event.target.value)}
                  required
                  value={form.name}
                />
              </Field>

              <Field htmlFor="project-id" label="Project ID">
                <input
                  disabled={!isNew}
                  id="project-id"
                  onChange={(event) => updateField("id", event.target.value.toLowerCase())}
                  placeholder="project-name"
                  required
                  value={form.id}
                />
              </Field>

              <Field htmlFor="project-category" label="Category">
                <input
                  id="project-category"
                  onChange={(event) => updateField("category", event.target.value)}
                  required
                  value={form.category}
                />
              </Field>

              <Field htmlFor="project-order" label="Display order">
                <input
                  id="project-order"
                  min="0"
                  onChange={(event) => updateField("order", event.target.value)}
                  required
                  step="1"
                  type="number"
                  value={form.order}
                />
              </Field>

              <Field htmlFor="project-url" label="Live URL" wide>
                <input
                  id="project-url"
                  onChange={(event) => updateField("url", event.target.value)}
                  placeholder="https://"
                  required
                  type="url"
                  value={form.url}
                />
              </Field>

              <Field htmlFor="project-description" label="Description" wide>
                <textarea
                  id="project-description"
                  onChange={(event) => updateField("description", event.target.value)}
                  required
                  rows={4}
                  value={form.description}
                />
              </Field>

              <Field htmlFor="project-result" label="Result" wide>
                <textarea
                  id="project-result"
                  onChange={(event) => updateField("result", event.target.value)}
                  rows={3}
                  value={form.result}
                />
              </Field>

              <Field htmlFor="project-stack" label="Stack (comma separated)" wide>
                <input
                  id="project-stack"
                  onChange={(event) => updateField("stack", event.target.value)}
                  value={form.stack}
                />
              </Field>

              <Field htmlFor="project-features" label="Features (one per line)" wide>
                <textarea
                  id="project-features"
                  onChange={(event) => updateField("features", event.target.value)}
                  rows={4}
                  value={form.features}
                />
              </Field>

              <Field htmlFor="project-images" label="Project Images" wide>
                <div
                  className={`admin-dropzone ${isDragging ? "is-dragging" : ""} ${uploading ? "is-uploading" : ""}`}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <input
                    accept="image/*"
                    disabled={uploading}
                    id="project-file-upload"
                    multiple
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        handleFileUpload(e.target.files);
                        e.target.value = "";
                      }
                    }}
                    style={{ display: "none" }}
                    type="file"
                  />
                  <label htmlFor="project-file-upload" className="admin-dropzone-content">
                    {uploading ? (
                      <>
                        <LoaderCircle className="admin-spin" size={26} />
                        <div>
                          <strong>Uploading to Cloudflare R2...</strong>
                          <span>{uploadStatus}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <UploadCloud size={28} />
                        <div>
                          <strong>Click to select or drag images here</strong>
                          <span>Direct upload to Cloudflare R2 bucket • PNG, JPG, WebP</span>
                        </div>
                      </>
                    )}
                  </label>
                </div>

                {splitLines(form.images).length > 0 && (
                  <div className="admin-image-thumbnails">
                    {splitLines(form.images).map((imgUrl, index) => (
                      <div className="admin-image-card" key={`${imgUrl}-${index}`}>
                        <img alt={`Project ${index + 1}`} src={imgUrl} />
                        <span className="admin-image-badge">#{index + 1}</span>
                        <button
                          aria-label="Remove image"
                          className="admin-image-delete"
                          onClick={() => removeImage(index)}
                          title="Remove image"
                          type="button"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="admin-image-urls-section">
                  <label htmlFor="project-images" className="admin-sublabel">
                    Image URLs (Direct URLs / One per line)
                  </label>
                  <textarea
                    id="project-images"
                    onChange={(event) => updateField("images", event.target.value)}
                    placeholder="https://pub-2520cfe8f11f4f9ab4d2e59663aef88e.r2.dev/..."
                    required
                    rows={4}
                    value={form.images}
                  />
                </div>
              </Field>
            </div>

            <div className="admin-publish-row">
              <div>
                <strong>Published</strong>
                <span>{form.published ? "Visible on the portfolio" : "Saved as a draft"}</span>
              </div>
              <label className="admin-switch" htmlFor="project-published">
                <input
                  checked={form.published}
                  id="project-published"
                  onChange={(event) => updateField("published", event.target.checked)}
                  type="checkbox"
                />
                <span aria-hidden="true" />
              </label>
            </div>

            <div className="admin-preview">
              <div className="admin-preview-media">
                {previewImage ? (
                  <img alt="Project preview" src={previewImage} />
                ) : (
                  <FolderKanban size={30} />
                )}
              </div>
              <div>
                <p className="admin-eyebrow">Preview</p>
                <strong>{form.name || "Untitled project"}</strong>
                <span>{form.category || "No category"}</span>
              </div>
            </div>
          </form>
        </section>
      </div>
    </main>
  );
}

export default function AdminPanel() {
  const [access, setAccess] = useState<AccessState>("checking");
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const auth = firebaseAuth;
    const db = firestoreDb;

    if (!isFirebaseConfigured || !auth || !db) {
      setAccess("error");
      return;
    }

    return onAuthStateChanged(auth, async (nextUser) => {
      setUser(nextUser);

      if (!nextUser) {
        setAccess("signed-out");
        return;
      }

      setAccess("checking");

      try {
        const adminRecord = await getDoc(doc(db, "admins", nextUser.uid));
        setAccess(adminRecord.exists() ? "authorized" : "denied");
      } catch {
        setAccess("denied");
      }
    });
  }, []);

  if (access === "checking") {
    return (
      <main className="admin-state-page" aria-label="Checking access">
        <LoaderCircle className="admin-spin" size={28} />
      </main>
    );
  }

  if (access === "signed-out") {
    return <AdminLogin />;
  }

  if (access === "authorized" && user) {
    return <AdminDashboard user={user} />;
  }

  if (access === "denied" && user) {
    return <AccessDenied user={user} />;
  }

  return (
    <main className="admin-state-page">
      <LockKeyhole size={28} />
      <h1>Admin is not configured.</h1>
    </main>
  );
}
